import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const STATUS_COLOURS = {
  draft: '#aaa',
  generating: '#E97132',
  generated: '#e6a817',
  complete: '#196B24',
  error: '#c0392b',
};

export default function DashboardPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('dock_user') || '{}');

  useEffect(() => {
    api.get('/reports/').then(r => setReports(r.data.filter(r => r.status !== 'complete'))).finally(() => setLoading(false));
  }, []);

  async function deleteReport(e, reportId) {
    e.stopPropagation();
    if (!window.confirm('Delete this report and all its data?')) return;
    try {
      await api.delete(`/reports/${reportId}`);
      setReports(prev => prev.filter(r => r.id !== reportId));
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
  }

  async function markComplete(e, reportId) {
    e.stopPropagation();
    if (!window.confirm('Mark as complete? All source files uploaded for this report will be permanently deleted. Make sure you have already downloaded your report.')) return;
    try {
      await api.post(`/reports/${reportId}/complete`);
      setReports(prev => prev.map(r => r.id === reportId ? { ...r, status: 'complete' } : r));
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
  }

  function handleLogout() {
    localStorage.removeItem('dock_token');
    localStorage.removeItem('dock_user');
    window.location.href = '/login';
  }

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.headerLogo}>DOCK</span>
          <span style={styles.headerSub}>Documentation Kit</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.userName}>{user.name}</span>
          <button style={styles.logoutBtn} onClick={handleLogout}>Logout</button>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.toolbar}>
          <h1 style={styles.title}>My Reports</h1>
          <button style={styles.newBtn} onClick={() => navigate('/reports/new')}>
            + New Report
          </button>
        </div>

        {loading && <p style={styles.empty}>Loading…</p>}
        {!loading && reports.length === 0 && (
          <div style={styles.emptyState}>
            <p style={styles.emptyText}>No reports yet.</p>
            <p style={styles.emptyHint}>Start by creating a new report for a completed project.</p>
          </div>
        )}

        <div style={styles.grid}>
          {reports.map(r => (
            <div key={r.id} style={styles.card} onClick={() => navigate(`/reports/${r.id}`)}>
              <div style={styles.cardHeader}>
                <div style={{display:'flex', alignItems:'center', gap:6}}>
                  <span style={{ ...styles.statusBadge, background: STATUS_COLOURS[r.status] || '#aaa' }}>
                    {r.status}
                  </span>
                  {r.status === 'generated' && (
                    <button style={styles.completeBtn} onClick={e => markComplete(e, r.id)}>
                      ✓ Complete
                    </button>
                  )}
                  {['draft', 'error'].includes(r.status) && (
                    <button style={styles.deleteReportBtn} onClick={e => deleteReport(e, r.id)} title="Delete">✕</button>
                  )}
                </div>
                <span style={styles.cardDate}>{r.created_at?.slice(0, 10)}</span>
              </div>
              <h3 style={styles.cardTitle}>{r.project_title}</h3>
              {r.client_name && <p style={styles.cardClient}>{r.client_name}</p>}
              {r.me_ticket_ref && <p style={styles.cardTicket}>ME: {r.me_ticket_ref}</p>}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f4f6f9', fontFamily: 'system-ui, sans-serif' },
  header: {
    background: '#0E2841', color: '#fff', padding: '0 32px',
    height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  headerLeft: { display: 'flex', alignItems: 'baseline', gap: 10 },
  headerLogo: { fontSize: 22, fontWeight: 800, letterSpacing: 4 },
  headerSub: { fontSize: 11, color: '#89a8c0', letterSpacing: 2, textTransform: 'uppercase' },
  headerRight: { display: 'flex', alignItems: 'center', gap: 16 },
  userName: { fontSize: 14, color: '#cdd9e5' },
  logoutBtn: {
    background: 'transparent', border: '1px solid #89a8c0', color: '#cdd9e5',
    padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
  },
  main: { maxWidth: 1100, margin: '0 auto', padding: '32px 24px' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { margin: 0, fontSize: 24, color: '#0E2841', fontWeight: 700 },
  newBtn: {
    background: '#156082', color: '#fff', border: 'none',
    padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 },
  card: {
    background: '#fff', borderRadius: 10, padding: 20,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', cursor: 'pointer',
    transition: 'box-shadow 0.2s',
  },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  statusBadge: { fontSize: 11, color: '#fff', padding: '2px 8px', borderRadius: 10, fontWeight: 600, textTransform: 'uppercase' },
  cardDate: { fontSize: 12, color: '#aaa' },
  cardTitle: { margin: '0 0 6px', fontSize: 16, color: '#0E2841', fontWeight: 600 },
  cardClient: { margin: '0 0 4px', fontSize: 13, color: '#555' },
  cardTicket: { margin: 0, fontSize: 12, color: '#156082' },
  deleteReportBtn: {
    background: 'transparent', border: 'none', color: '#ccc',
    cursor: 'pointer', fontSize: 13, padding: '2px 6px', borderRadius: 6,
  },
  completeBtn: {
    padding: '3px 10px',
    background: 'transparent', color: '#196B24', border: '1.5px solid #196B24',
    borderRadius: 10, fontSize: 11, fontWeight: 700, cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  empty: { color: '#aaa', textAlign: 'center', marginTop: 40 },
  emptyState: { textAlign: 'center', marginTop: 80 },
  emptyText: { fontSize: 18, color: '#555', margin: 0 },
  emptyHint: { fontSize: 14, color: '#aaa', marginTop: 8 },
};
